import { useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, CalendarDays } from "lucide-react";
import { Reveal } from "@/components/site/Reveal";
import { BLOG_POSTS, BLOG_AUTHOR } from "@/blogData";

const SITE = "https://heritagefamilypa.com";

export default function BlogPostPage() {
  const { slug } = useParams();
  const post = BLOG_POSTS.find((p) => p.slug === slug);

  useEffect(() => {
    if (!post) return;
    document.title = post.metaTitle;
    document.querySelector('meta[name="description"]')?.setAttribute("content", post.metaDescription);
    document.querySelector('link[rel="canonical"]')?.setAttribute("href", `${SITE}/blog/${post.slug}`);

    const ld = document.createElement("script");
    ld.type = "application/ld+json";
    ld.id = "blog-post-ld";
    ld.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Article",
      headline: post.title,
      description: post.metaDescription,
      image: post.image,
      author: { "@type": "Organization", name: BLOG_AUTHOR },
      publisher: { "@type": "Organization", name: BLOG_AUTHOR, logo: { "@type": "ImageObject", url: `${SITE}/heritage-logo.png` } },
      mainEntityOfPage: `${SITE}/blog/${post.slug}`,
    });
    document.head.appendChild(ld);
    return () => document.getElementById("blog-post-ld")?.remove();
  }, [post]);

  if (!post) return <Navigate to="/blog" replace />;

  const related = BLOG_POSTS.filter((p) => p.slug !== slug).slice(0, 3);

  return (
    <>
      <section className="relative pt-32 md:pt-40 pb-12">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <Reveal>
            <Link to="/blog" data-testid="blog-back-link" className="inline-flex items-center gap-2 font-body text-sm text-white/50 hover:text-[#d4af37] transition-colors">
              <ArrowLeft size={15} /> All Articles
            </Link>
            <p className="mt-6 font-body text-xs md:text-sm tracking-[0.3em] uppercase text-[#d4af37]">{post.category}</p>
            <h1 data-testid="blog-post-title" className="mt-4 font-serif-display text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight leading-[1.05] text-white">
              {post.title}
            </h1>
            <p className="mt-5 font-body text-sm text-white/45">
              By {BLOG_AUTHOR} · {post.date} · {post.readTime}
            </p>
          </Reveal>
        </div>
      </section>

      <section className="relative pb-8">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <Reveal>
            <img src={post.image} alt={post.title} className="w-full h-72 md:h-[420px] object-cover rounded-3xl border border-[#bf953f]/20" />
          </Reveal>
        </div>
      </section>

      <article className="relative py-10 md:py-14">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          {post.sections.map((s, i) => (
            <Reveal key={i} delay={Math.min(i, 3) * 0.04}>
              <h2 className="mt-10 first:mt-0 font-serif-display text-3xl md:text-4xl font-medium tracking-tight text-white">
                {s.heading}
              </h2>
              {s.paragraphs.map((p, j) => (
                <p key={j} className="mt-5 font-body font-light text-base md:text-lg leading-relaxed text-white/65">{p}</p>
              ))}
            </Reveal>
          ))}

          <Reveal>
            <div className="mt-14 rounded-3xl border border-[#bf953f]/30 bg-gradient-to-br from-[#12284a] to-[#0a192f] p-8 md:p-10 text-center">
              <h3 className="font-serif-display text-3xl md:text-4xl text-white">
                Ready to protect <span className="text-gold-gradient italic">your family?</span>
              </h3>
              <p className="mt-3 mx-auto max-w-md font-body font-light text-sm md:text-base text-white/60">
                Get a free, no-pressure quote or book a call at a time that works for you.
              </p>
              <div className="mt-7 flex flex-wrap justify-center gap-4">
                <Link
                  to="/life-insurance#quote"
                  data-testid="blog-cta-quote"
                  className="btn-sweep inline-flex items-center gap-2 rounded-full bg-gold-metallic px-7 py-3 font-body text-sm font-semibold tracking-wide text-black"
                >
                  Get My Free Quote <ArrowUpRight size={15} />
                </Link>
                <Link
                  to="/book-a-call"
                  data-testid="blog-cta-book"
                  className="inline-flex items-center gap-2 rounded-full border border-[#bf953f]/40 px-7 py-3 font-body text-sm tracking-wide text-white/80 hover:text-[#fcf6ba] hover:border-[#d4af37] transition-colors duration-300"
                >
                  <CalendarDays size={15} /> Book a Call
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </article>

      <section className="relative py-16 md:py-20 bg-[#0a192f] border-t border-[#bf953f]/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <h3 className="font-serif-display text-3xl md:text-4xl text-white">Keep <span className="text-gold-gradient italic">reading.</span></h3>
          </Reveal>
          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
            {related.map((p, i) => (
              <Reveal key={p.slug} delay={i * 0.06}>
                <Link
                  to={`/blog/${p.slug}`}
                  data-testid={`blog-related-${p.slug}`}
                  className="group block overflow-hidden rounded-2xl border border-white/10 bg-black/40 hover:border-[#bf953f]/45 transition-colors duration-500"
                >
                  <img src={p.image} alt={p.title} className="h-36 w-full object-cover transition-transform duration-700 group-hover:scale-[1.05]" loading="lazy" />
                  <div className="p-5">
                    <p className="font-body text-[11px] tracking-widest uppercase text-[#d4af37]">{p.category}</p>
                    <h4 className="mt-2 font-serif-display text-lg leading-snug text-white">{p.title}</h4>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
